import { useState } from "react";
import { useNavigate } from "react-router-dom";
import OnboardingLayout from "../../components/onboarding/OnboardingLayout";
import BusinessDetailsStep from "./steps/BusinessDetailsStep";
import BusinessSettingsStep from "./steps/BusinessSettingsStep";
import GoogleConnectStep from "./steps/GoogleConnectStep";
import FinishSetupStep from "./steps/FinishSetupStep";
import {
  createBusiness,
  updateBusinessSettings,
} from "../../lib/businessService";

export default function BusinessOnboarding() {
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [businessId, setBusinessId] = useState(null);
  const [createdBusiness, setCreatedBusiness] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [businessForm, setBusinessForm] = useState({
    name: "",
    industry: "",
    customIndustry: "",
    email: "",
    website: "",
    phone: "",
  });

  const [settingsForm, setSettingsForm] = useState({
    tone: "friendly",
    responseLength: "medium",
    autoApprovePositive: false,
    requireManualNegative: true,
    includePhone: false,
    includeEmail: true,
    includeWebsite: false,
    includeSignature: true,
    signatureText: "",
    avoidRepetitivePhrases: true,
  });

  function getErrorMessage(err, fallback) {
    const data = err.response?.data;

    if (typeof data === "string") return data;
    if (data?.message) return data.message;
    if (data?.title) return data.title;

    if (data?.errors) {
      const firstError = Object.values(data.errors)?.[0]?.[0];
      if (firstError) return firstError;
    }

    return fallback;
  }

  function handleBusinessChange(e) {
    const { name, value } = e.target;

    setBusinessForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  }

  function handleSettingsChange(e) {
    const { name, value, type, checked } = e.target;

    setSettingsForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  }

  async function handleCreateBusiness(e) {
    e.preventDefault();

    setError("");
    setLoading(true);

    try {
      const finalIndustry =
        businessForm.industry === "Other"
          ? businessForm.customIndustry
          : businessForm.industry;

      const business = await createBusiness({
        name: businessForm.name,
        industry: finalIndustry,
        email: businessForm.email,
        website: businessForm.website,
        phone: businessForm.phone,
      });

      setBusinessId(business.id);
      setCreatedBusiness(business);
      setStep(2);
    } catch (err) {
      setError(
        getErrorMessage(err, "Unable to create business. Please try again."),
      );
    } finally {
      setLoading(false);
    }
  }

  async function handleSaveSettings(e) {
    e.preventDefault();

    setError("");

    if (!businessId) {
      setError("Business was not created yet.");
      return;
    }

    setLoading(true);

    try {
      await updateBusinessSettings(businessId, settingsForm);
      setStep(3);
    } catch (err) {
      setError(
        getErrorMessage(err, "Unable to save settings. Please try again."),
      );
    } finally {
      setLoading(false);
    }
  }

  function handleGoogleConnect() {
    // Placeholder until your real Google OAuth endpoint is ready.
    // Later this can become:
    // window.location.href = "https://localhost:7177/api/google/connect";
    setStep(4);
  }

  function resetForAnotherBusiness() {
    setBusinessId(null);
    setCreatedBusiness(null);
    setError("");

    setBusinessForm({
      name: "",
      industry: "",
      email: "",
    });

    setSettingsForm({
      tone: "friendly",
      responseLength: "medium",
      autoApprovePositive: false,
      requireManualNegative: true,
      includePhone: false,
      includeEmail: true,
      includeWebsite: false,
      includeSignature: true,
      signatureText: "",
      avoidRepetitivePhrases: true,
    });

    setStep(1);
  }

  if (step === 1) {
    return (
      <OnboardingLayout
        step={1}
        title="Add your business"
        subtitle="This helps RevUShield personalize review responses for your brand."
      >
        <BusinessDetailsStep
          form={businessForm}
          onChange={handleBusinessChange}
          onNext={handleCreateBusiness}
          loading={loading}
          error={error}
        />
      </OnboardingLayout>
    );
  }

  if (step === 2) {
    return (
      <OnboardingLayout
        step={2}
        title="Set your response preferences"
        subtitle="Choose how RevUShield should draft responses for this business."
      >
        <BusinessSettingsStep
          settings={settingsForm}
          onChange={handleSettingsChange}
          onBack={() => setStep(1)}
          onFinish={handleSaveSettings}
          loading={loading}
          error={error}
        />
      </OnboardingLayout>
    );
  }

  if (step === 3) {
    return (
      <OnboardingLayout
        step={3}
        title="Connect Google"
        subtitle="Connect your Google Business Profile now, or skip and do it later."
      >
        <GoogleConnectStep
          loading={loading}
          error={error}
          onBack={() => setStep(2)}
          onSkip={() => setStep(4)}
          onConnect={handleGoogleConnect}
        />
      </OnboardingLayout>
    );
  }

  return (
    <OnboardingLayout
      step={4}
      title="Setup complete"
      subtitle="You can now start using RevUShield."
    >
      <FinishSetupStep
        business={createdBusiness}
        onDashboard={() => navigate("/dashboard")}
        onAddAnother={resetForAnotherBusiness}
      />
    </OnboardingLayout>
  );
}
